import * as styles from "./Tour.css";

import { type JSX, useCallback, useEffect } from "react";

import {
  Popover,
  PopoverContent,
  type PopoverProps,
} from "@frontend/components/atoms/Popover";

import type { TourProps } from "./Tour.types";
import { TourCard } from "./TourCard";
import { TourOverlay } from "./TourOverlay";
import { TOUR_PADDING } from "./constants";
import { useTourTarget } from "./useTourTarget";

const viewportCenter = {
  getBoundingClientRect: () =>
    new DOMRect(window.innerWidth / 2, window.innerHeight * 0.4, 0, 0),
};

export function Tour({
  steps,
  open,
  step,
  onStepChange,
  onClose,
}: TourProps): JSX.Element | null {
  const current = steps[step];
  const active = open && current !== undefined;

  const target = useTourTarget(current?.target, active, current?.clip);

  const goNext = useCallback(() => {
    if (step < steps.length - 1) {
      onStepChange(step + 1);

      return;
    }

    onClose("complete");
  }, [step, steps.length, onStepChange, onClose]);

  const goPrevious = useCallback(() => {
    if (step > 0) {
      onStepChange(step - 1);
    }
  }, [step, onStepChange]);

  useEffect(() => {
    if (!active) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "ArrowRight") {
        event.preventDefault();

        goNext();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();

        goPrevious();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [active, goNext, goPrevious]);

  const gesture = current?.interactive;
  const targetElement = target?.element;

  useEffect(() => {
    if (!active || !gesture || !targetElement) {
      return;
    }

    const swallowed = gesture === "click" ? "contextmenu" : "click";

    function swallow(event: Event) {
      if (!targetElement || !event.composedPath().includes(targetElement)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    }

    document.addEventListener(swallowed, swallow, true);

    return () => document.removeEventListener(swallowed, swallow, true);
  }, [active, gesture, targetElement]);

  if (!active) {
    return null;
  }

  const handleOpenChange: PopoverProps["onOpenChange"] = (
    nextOpen,
    eventDetails,
  ) => {
    if (nextOpen) {
      return;
    }

    if (eventDetails.reason === "escape-key") {
      onClose("skip");

      return;
    }

    eventDetails.cancel();
  };

  const padding = current.padding ?? TOUR_PADDING;

  return (
    <>
      <TourOverlay
        rect={target?.rect ?? null}
        step={step}
        padding={padding}
        radius={current.radius}
        reachable={Boolean(gesture)}
      />

      <Popover
        open
        modal={gesture ? false : "trap-focus"}
        onOpenChange={handleOpenChange}
      >
        <PopoverContent
          anchor={target?.element ?? viewportCenter}
          side={current.side ?? "bottom"}
          align={current.align ?? "center"}
          sideOffset={padding + 8}
          arrow={target !== null}
          className={styles.card}
        >
          <TourCard
            step={current}
            index={step}
            total={steps.length}
            onNext={goNext}
            onPrevious={goPrevious}
            onSkip={() => onClose("skip")}
          />
        </PopoverContent>
      </Popover>
    </>
  );
}
